import React from 'react';
import siteConfig from '../../config/siteConfig';

// Utility to validate URLs
const isValidUrl = (urlString: string): boolean => {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isValidISO8601, cleanSchema, isValidUrl as utilsIsValidUrl } from '../../lib/utils';

// Keep your existing isValidUrl function OR replace it with the imported one
// If you want to use the imported version, replace your isValidUrl function with:
// const isValidUrl = utilsIsValidUrl;

// Define interfaces for props and schema structures
interface Author {
  name: string;
  jobTitle: string;
  id: string;
}

interface BlogPostProps {
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author: Author;
  image: string;
  url: string;
}

interface Question {
  question: string;
  answer: string;
}

interface FAQProps {
  questions: Question[];
}

interface Offer {
  name: string;
  description: string;
  price: number | string;
  id: string;
}

interface ProductProps {
  name: string;
  description: string;
  image: string;
  offers: Offer[];
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

interface ServiceProps {
  name: string;
  description: string;
  url: string;
}

interface CaseStudyProps {
  name: string;
  description: string;
  url: string;
  organization: string;
}

interface AuthorPageProps {
  author: {
    name: string;
    jobTitle: string;
    bio: string;
    id: string;
    linkedin?: string;
    image?: string;
  };
  articles: Array<{
    title: string;
    excerpt: string;
    id: string;
  }>;
}

// Organization schema for Perxify
export const OrganizationSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.organization.name,
    url: baseUrl,
    logo: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
    sameAs: [
      isValidUrl(siteConfig.organization.social.linkedin) ? siteConfig.organization.social.linkedin : null,
      isValidUrl(siteConfig.organization.social.x) ? siteConfig.organization.social.x : null,
      isValidUrl(siteConfig.organization.social.github) ? siteConfig.organization.social.github : null,
    ].filter(Boolean), // Remove invalid/null URLs
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteConfig.organization.contact.email,
      contactType: siteConfig.organization.contact.type,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: siteConfig.organization.address.office.locality,
      addressCountry: siteConfig.organization.address.office.country,
    },
    description: siteConfig.organization.description,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(organizationSchema) }}
    />
  );
};

// Website schema for Perxify
export const WebsiteSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.organization.name,
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}${siteConfig.search.path}`,
      'query-input': siteConfig.search.queryInput,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(websiteSchema) }}
    />
  );
};

// Blog post schema for Perxify blog articles
export const BlogPostSchema: React.FC<BlogPostProps> = ({ title, description, datePublished, dateModified, author, image, url }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const validUrl = isValidUrl(url) ? url : baseUrl;
  const validImage = isValidUrl(image) ? image : `${baseUrl}/images/default-blog-image.jpg`;
  const currentDate = new Date().toISOString();
  const validDatePublished = isValidISO8601(datePublished) ? datePublished : currentDate;
  const validDateModified = dateModified && isValidISO8601(dateModified) ? dateModified : validDatePublished;

  const blogPostSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: description,
    image: validImage,
    datePublished: validDatePublished,
    dateModified: validDateModified,
    author: {
      '@type': 'Person',
      name: author.name,
      jobTitle: author.jobTitle,
      url: isValidUrl(`${siteConfig.baseUrl}/team/${author.id}`) ? `${siteConfig.baseUrl}/team/${author.id}` : baseUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': validUrl,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(blogPostSchema) }}
    />
  );
};

// FAQ schema for Perxify FAQ sections
export const FAQSchema: React.FC<FAQProps> = ({ questions }) => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question.trim(), // Ensure no leading/trailing whitespace
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer.trim(), // Ensure no leading/trailing whitespace
      },
    })),
  };

  // Explicitly serialize to ensure consistency
  const schemaString = JSON.stringify(faqSchema, (key, value) => {
    if (value === undefined) return null;
    return typeof value === 'string' ? value.trim() : value;
  });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: schemaString }}
    />
  );
};

// Product schema for Perxify product/service offerings
export const ProductSchema: React.FC<ProductProps> = ({ name, description, image, offers }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const validImage = isValidUrl(image) ? image : `${baseUrl}/images/default-product-image.jpg`;
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: name,
    description: description,
    image: validImage,
    offers: offers.map(offer => ({
      '@type': 'Offer',
      name: offer.name,
      description: offer.description,
      price: offer.price,
      priceCurrency: siteConfig.pricing.currency,
      url: isValidUrl(`${siteConfig.baseUrl}/pricing#${offer.id}`) ? `${siteConfig.baseUrl}/pricing#${offer.id}` : baseUrl,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(productSchema) }}
    />
  );
};

// BreadcrumbList schema for navigation paths
export const BreadcrumbSchema: React.FC<BreadcrumbProps> = ({ items }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: isValidUrl(item.url) ? item.url : baseUrl,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(breadcrumbSchema) }}
    />
  );
};

// Service schema for Perxify services
export const ServiceSchema: React.FC<ServiceProps> = ({ name, description, url }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const validUrl = isValidUrl(url) ? url : baseUrl;
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: name,
    description: description,
    provider: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      url: baseUrl,
    },
    url: validUrl,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(serviceSchema) }}
    />
  );
};

// Case Study schema for Perxify case studies
export const CaseStudySchema: React.FC<CaseStudyProps> = ({ name, description, url, organization }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const validUrl = isValidUrl(url) ? url : baseUrl;
  const caseStudySchema = {
    '@context': 'https://schema.org',
    '@type': 'CaseStudy',
    name: name,
    description: description,
    url: validUrl,
    about: {
      '@type': 'Organization',
      name: organization,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': validUrl,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(caseStudySchema) }}
    />
  );
};

// About Page schema for Perxify
export const AboutPageSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'About Perxify',
    url: isValidUrl(`${siteConfig.baseUrl}/about`) ? `${siteConfig.baseUrl}/about` : baseUrl,
    description: 'Learn about Perxify\'s mission to build a Commerce Cloud for the Agentic Web, uniting brands and shoppers in an AI-powered ecosystem.',
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      description: siteConfig.organization.description,
      url: baseUrl,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(aboutPageSchema) }}
    />
  );
};

// Contact Page schema for Perxify
export const ContactPageSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const contactPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Contact Perxify',
    url: isValidUrl(`${siteConfig.baseUrl}/contact`) ? `${siteConfig.baseUrl}/contact` : baseUrl,
    description: 'Get in touch with Perxify for demos, support, or inquiries about our Commerce Cloud for the Agentic Web.',
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      url: baseUrl,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'Customer Service',
          email: siteConfig.organization.contact.email,
          url: isValidUrl(`${siteConfig.baseUrl}/contact`) ? `${siteConfig.baseUrl}/contact` : baseUrl,
        },
        {
          '@type': 'ContactPoint',
          contactType: 'Sales',
          email: siteConfig.organization.contact.email,
          url: isValidUrl(`${siteConfig.baseUrl}/contact/schedule-demo`) ? `${siteConfig.baseUrl}/contact/schedule-demo` : baseUrl,
        },
      ],
      address: {
        '@type': 'PostalAddress',
        addressLocality: siteConfig.organization.address.office.locality,
        addressCountry: siteConfig.organization.address.office.country,
      },
      sameAs: [
        isValidUrl(siteConfig.organization.social.linkedin) ? siteConfig.organization.social.linkedin : null,
        isValidUrl(siteConfig.organization.social.x) ? siteConfig.organization.social.x : null,
      ].filter(Boolean), // Remove invalid/null URLs
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(contactPageSchema) }}
    />
  );
};

// Blog Page schema for Perxify
export const BlogPageSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const blogPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Perxify Blog',
    url: isValidUrl(`${siteConfig.baseUrl}/blog`) ? `${siteConfig.baseUrl}/blog` : baseUrl,
    description: 'Explore articles and insights on commerce and the Agentic Web from Perxify.',
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(blogPageSchema) }}
    />
  );
};

// Team Page schema for Perxify
export const TeamPageSchema: React.FC = () => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const teamPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Perxify Team',
    url: isValidUrl(`${siteConfig.baseUrl}/team`) ? `${siteConfig.baseUrl}/team` : baseUrl,
    description: 'Meet the team behind Perxify\'s Commerce Cloud for the Agentic Web.',
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      url: baseUrl,
      description: siteConfig.organization.description,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(teamPageSchema) }}
    />
  );
};

// Author Page schema for individual team member pages
export const AuthorPageSchema: React.FC<AuthorPageProps> = ({ author, articles }) => {
  const baseUrl = isValidUrl(siteConfig.baseUrl) ? siteConfig.baseUrl : 'https://perxify.com/';
  const authorUrl = `${baseUrl}/team/${author.id}`;
  const authorImage = author.image ? (isValidUrl(author.image) ? author.image : `${baseUrl}${author.image}`) : `${baseUrl}/images/team/default-avatar.jpg`;
  
  const authorPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: `${author.name} - ${author.jobTitle}`,
    url: authorUrl,
    description: `Learn about ${author.name}, ${author.jobTitle} at Perxify. ${author.bio}`,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.organization.name,
      logo: {
        '@type': 'ImageObject',
        url: isValidUrl(siteConfig.logo.url) ? siteConfig.logo.url : `${baseUrl}/images/logo.png`,
      },
    },
    mainEntity: {
      '@type': 'Person',
      name: author.name,
      jobTitle: author.jobTitle,
      description: author.bio,
      url: authorUrl,
      image: authorImage,
      worksFor: {
        '@type': 'Organization',
        name: siteConfig.organization.name,
        url: baseUrl,
      },
      ...(author.linkedin && isValidUrl(author.linkedin) && {
        sameAs: [author.linkedin]
      }),
      ...(articles.length > 0 && {
        mainEntityOfPage: articles.map(article => ({
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          url: `${baseUrl}/blog/${article.id}`,
          author: {
            '@type': 'Person',
            name: author.name,
            jobTitle: author.jobTitle,
          }
        }))
      })
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: cleanSchema(authorPageSchema) }}
    />
  );
};

// Combined schema component for homepage
export const HomePageSchema: React.FC = () => {
  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
    </>
  );
};

// Export components with default object
export default {
  OrganizationSchema,
  WebsiteSchema,
  BlogPostSchema,
  FAQSchema,
  ProductSchema,
  BreadcrumbSchema,
  ServiceSchema,
  CaseStudySchema,
  AboutPageSchema,
  ContactPageSchema,
  BlogPageSchema,
  TeamPageSchema,
  AuthorPageSchema,
  HomePageSchema,
};